import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  IconButton,
  Spinner,
  Stack,
  Text,
  Textarea,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { LuSparkles } from 'react-icons/lu';
import { FiMic, FiTrash2 } from 'react-icons/fi';

import {
  cancelAiAction,
  confirmAiAction,
  createAiSession,
  deleteAiSession,
  getAiCapabilities,
  getAiSession,
  getAiSessionMessages,
  postAiMessage,
} from '../services/aiService';
import { AI_UI_EVENT_TYPES, trackAiEvent } from '../services/aiAnalytics';
import { applyAiHandoffRoute } from '../utils/handoff';
import { isAiAssistantEnabledForRole } from '../utils/availability';
import { clearAiSessionId, getAiPreferences, getAiSessionId, setAiSessionId } from '../utils/storage';
import { getPublicSettings } from 'services/settingsService';
import AiResultRenderer from './AiResultRenderer';
import AiVoiceModal from './AiVoiceModal';

function getErrorStatus(error) {
  return typeof error?.response?.status === 'number' ? error.response.status : null;
}

function getPreparedMessage(routeKey) {
  switch (routeKey) {
    case 'client.messages.thread':
    case 'partner.messages.thread':
      return 'Prepared drafted reply in the message thread.';
    case 'client.listings.edit':
      return 'Prepared listing edits in the existing editor.';
    case 'client.reviews.create':
      return 'Prepared a review draft for final edits.';
    case 'partner.marketplace':
      return 'Prepared marketplace filters in the existing search view.';
    case 'partner.jobs.detail':
    case 'client.listings.detail':
      return 'Prepared the related detail view for review.';
    case 'partner.fleet':
      return 'Prepared the fleet workspace for review.';
    case 'partner.invoices':
      return 'Prepared the invoice workspace for review.';
    default:
      return `Prepared handoff: ${String(routeKey || '').replace(/\./g, ' → ')}`;
  }
}

function getPreparedButtonLabel(routeKey) {
  switch (routeKey) {
    case 'client.messages.thread':
    case 'partner.messages.thread':
      return 'Review drafted reply';
    case 'client.listings.edit':
      return 'Continue editing listing';
    case 'client.reviews.create':
      return 'Review drafted review';
    case 'partner.marketplace':
      return 'Open prepared search';
    case 'partner.jobs.detail':
    case 'client.listings.detail':
      return 'Open prepared details';
    case 'partner.fleet':
      return 'Open fleet workspace';
    case 'partner.invoices':
      return 'Open invoice workspace';
    default:
      return 'Open prepared screen';
  }
}

function AssistantMessage({ message }) {
  const isUser = message.role === 'user';
  const tone =
    message.kind === 'refusal'
      ? { bg: 'orange.50', borderColor: 'orange.200' }
      : isUser
        ? { bg: 'blue.500', borderColor: 'blue.500' }
        : { bg: 'white', borderColor: 'gray.200' };
  const color = isUser ? 'white' : 'navy.700';

  return (
    <Box borderWidth="1px" borderRadius="2xl" px={4} py={3} bg={tone.bg} borderColor={tone.borderColor}>
      {!isUser && message.kind && message.kind !== 'text' ? (
        <Text mb={1} fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.2em" color="blue.500">
          {String(message.kind).replace(/_/g, ' ')}
        </Text>
      ) : null}
      <Text fontSize="sm" lineHeight="1.7" color={color}>
        {message.content}
      </Text>
    </Box>
  );
}

function ProposalCard({ action, onConfirm, onCancel, busy }) {
  return (
    <Box borderWidth="1px" borderRadius="2xl" p={4} bg="blue.50" borderColor="blue.200">
      <Stack spacing={3}>
        <Box>
          <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.2em" color="blue.500">
            Awaiting confirmation
          </Text>
          <Text mt={1} fontSize="lg" fontWeight="800" color="navy.700">
            {action.preview?.title || 'Review proposed action'}
          </Text>
          {action.preview?.summary ? (
            <Text mt={1} fontSize="sm" color="gray.600">
              {action.preview.summary}
            </Text>
          ) : null}
        </Box>

        {Array.isArray(action.preview?.fields) && action.preview.fields.length ? (
          <Stack spacing={2}>
            {action.preview.fields.map((field) => (
              <HStack key={`${field.label}-${String(field.value)}`} justify="space-between" align="flex-start" spacing={3}>
                <Text flex="1" fontSize="sm" color="gray.500">
                  {field.label}
                </Text>
                <Text flex="1" fontSize="sm" fontWeight="700" color="navy.700" textAlign="right">
                  {String(field.value ?? '')}
                </Text>
              </HStack>
            ))}
          </Stack>
        ) : null}

        <HStack spacing={3}>
          <Button colorScheme="blue" onClick={onConfirm} isLoading={busy}>
            Confirm action
          </Button>
          <Button variant="outline" onClick={onCancel} isDisabled={busy}>
            Cancel
          </Button>
        </HStack>
      </Stack>
    </Box>
  );
}

export default function AiAssistantDrawer({
  role,
  screenContext,
  buttonLabel = 'AI assistant',
  title = 'AI assistant',
  subtitle = 'Ask for help with search, drafting, and guided actions.',
  onHandoff,
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const toast = useToast();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [capabilities, setCapabilities] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [busyActionId, setBusyActionId] = useState(null);
  const [lastHandoffRoute, setLastHandoffRoute] = useState(null);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [, setMessageCount] = useState(0);
  const [oldestLoadedIndex, setOldestLoadedIndex] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const scrollAnchorRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const prevScrollHeightRef = useRef(0);

  const hasMoreMessages = oldestLoadedIndex > 0;

  // Derive lastResult from local messages — authoritative source, updated incrementally
  const lastResult = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const r = messages[i].metadata?.result;
      if (r?.type) return r;
    }
    return null;
  }, [messages]);
  const [statusMessage, setStatusMessage] = useState('');
  const [preferences, setPreferences] = useState(() => getAiPreferences());
  const [serverAiEnabled, setServerAiEnabled] = useState(false);
  const [userAiEnabled, setUserAiEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getPublicSettings()
      .then((settings) => {
        if (!cancelled) {
          setServerAiEnabled(settings.aiEnabled !== false);
          setUserAiEnabled(settings.userAiEnabled !== false);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Auto-scroll to the latest message (only for new messages appended at the bottom, not prepends)
  useEffect(() => {
    if (!prevScrollHeightRef.current) {
      scrollAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages, loading]);

  // Restore scroll position after prepending older messages
  useLayoutEffect(() => {
    if (prevScrollHeightRef.current > 0 && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      container.scrollTop = container.scrollHeight - prevScrollHeightRef.current;
      prevScrollHeightRef.current = 0;
    }
  }, [messages]);
  const assistantEnabled = isAiAssistantEnabledForRole(role);

  const activeProposal = useMemo(
    () =>
      [...(session?.actions || [])]
        .reverse()
        .find((action) => action.status === 'proposed' && action.confirmationRequired) || null,
    [session?.actions],
  );

  const createFreshSession = useCallback(async () => {
    const nextSession = await createAiSession({
      locale:
        (typeof window !== 'undefined' && window.localStorage.getItem('logiflow.language')) ||
        'en',
      currentScreen: screenContext,
    });
    setAiSessionId(role, nextSession.id);
    setSession(nextSession);
    return nextSession.id;
  }, [role, screenContext]);

  const loadSession = useCallback(async (sessionId) => {
    try {
      const nextSession = await getAiSession(sessionId);
      setSession(nextSession);
      const msgs = nextSession.messages || [];
      const total = nextSession.messageCount ?? msgs.length;
      setMessages(msgs);
      setMessageCount(total);
      setOldestLoadedIndex(Math.max(0, total - msgs.length));
      return nextSession;
    } catch (error) {
      if (getErrorStatus(error) !== 404) {
        throw error;
      }

      clearAiSessionId(role);
      setSession(null);
      setMessages([]);
      setMessageCount(0);
      setOldestLoadedIndex(0);
      const freshSessionId = await createFreshSession();
      const freshSession = await getAiSession(freshSessionId);
      setSession(freshSession);
      const freshMsgs = freshSession.messages || [];
      const freshTotal = freshSession.messageCount ?? freshMsgs.length;
      setMessages(freshMsgs);
      setMessageCount(freshTotal);
      setOldestLoadedIndex(Math.max(0, freshTotal - freshMsgs.length));
      setStatusMessage('Assistant session was refreshed.');
      return freshSession;
    }
  }, [createFreshSession, role]);

  const loadOlderMessages = useCallback(async () => {
    const sessionId = getAiSessionId(role);
    if (!sessionId || oldestLoadedIndex <= 0 || loadingMore) return;

    const PAGE_SIZE = 20;
    const newSkip = Math.max(0, oldestLoadedIndex - PAGE_SIZE);
    const fetchLimit = oldestLoadedIndex - newSkip;

    setLoadingMore(true);
    try {
      const container = scrollContainerRef.current;
      prevScrollHeightRef.current = container?.scrollHeight || 0;

      const olderMessages = await getAiSessionMessages(sessionId, { skip: newSkip, limit: fetchLimit });
      setMessages((prev) => [...olderMessages, ...prev]);
      setOldestLoadedIndex(newSkip);
    } catch (error) {
      console.warn('Failed to load older messages:', error.message);
      prevScrollHeightRef.current = 0;
    } finally {
      setLoadingMore(false);
    }
  }, [role, oldestLoadedIndex, loadingMore]);

  const ensureSession = useCallback(async () => {
    const currentSessionId = getAiSessionId(role);
    if (currentSessionId) {
      return currentSessionId;
    }

    return createFreshSession();
  }, [createFreshSession, role]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleStorageChange = (event) => {
      if (event.key && event.key !== 'logiflow.ai.preferences') {
        return;
      }

      setPreferences(getAiPreferences());
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Auto-dismiss error messages after 8 seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setErrorMessage('');
    setStatusMessage('');

    void (async () => {
      try {
        const [capabilityList, ensuredSessionId] = await Promise.all([
          getAiCapabilities(),
          ensureSession(),
        ]);

        if (cancelled) {
          return;
        }

        setCapabilities(capabilityList);
        await loadSession(ensuredSessionId);
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(error?.message || 'Failed to open AI assistant.');
          toast({
            title: error?.message || 'Failed to open AI assistant',
            status: 'error',
            duration: 3000,
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ensureSession, isOpen, loadSession, toast]);

  const handleHandoff = (handoffRoute) => {
    if (!handoffRoute) {
      return;
    }

    if (typeof onHandoff === 'function' && onHandoff(handoffRoute) === true) {
      void trackAiEvent({
        type: AI_UI_EVENT_TYPES.HANDOFF_OPENED,
        role,
        routeKey: screenContext?.routeKey,
        handoffRouteKey: handoffRoute.routeKey,
        metadata: {
          clientSurface: screenContext?.surface || null,
          uiMode: 'text',
        },
      });
      setStatusMessage(getPreparedMessage(handoffRoute.routeKey));
      onClose();
      return;
    }

    const applied = applyAiHandoffRoute({ handoffRoute, navigate });
    if (applied) {
      void trackAiEvent({
        type: AI_UI_EVENT_TYPES.HANDOFF_OPENED,
        role,
        routeKey: screenContext?.routeKey,
        handoffRouteKey: handoffRoute.routeKey,
        metadata: {
          clientSurface: screenContext?.surface || null,
          uiMode: 'text',
        },
      });
    }
    setStatusMessage(getPreparedMessage(handoffRoute.routeKey));
    onClose();
  };

  const buildCurrentScreen = useCallback((contextOverrides = {}) => ({
    ...screenContext,
    lastResult,
    ...contextOverrides,
  }), [screenContext, lastResult]);

  const handleSend = async (contentOverride, contextOverrides) => {
    const content = contentOverride ?? draft.trim();
    if (!content) {
      return;
    }

    setLoading(true);
    setErrorMessage('');
    setStatusMessage('');
    try {
      let sessionId = await ensureSession();
      let response;
      const currentScreen = buildCurrentScreen(contextOverrides);

      const locale = window.localStorage.getItem('logiflow.language') || 'en';

      try {
        response = await postAiMessage(sessionId, {
          message: content,
          currentScreen,
          locale,
        });
      } catch (error) {
        if (getErrorStatus(error) !== 404) {
          throw error;
        }

        clearAiSessionId(role);
        sessionId = await createFreshSession();
        response = await postAiMessage(sessionId, {
          message: content,
          currentScreen,
          locale,
        });
        setStatusMessage('Assistant session was refreshed before sending your request.');
      }

      void trackAiEvent({
        type: AI_UI_EVENT_TYPES.MESSAGE_SENT,
        role,
        routeKey: screenContext?.routeKey,
        resultKind: response?.assistantMessage?.kind || null,
        metadata: {
          clientSurface: screenContext?.surface || null,
          uiMode: 'text',
        },
      });
      if (response?.assistantMessage?.kind === 'refusal') {
        void trackAiEvent({
          type: AI_UI_EVENT_TYPES.REFUSAL_SHOWN,
          role,
          routeKey: screenContext?.routeKey,
          resultKind: response.assistantMessage.kind,
          metadata: {
            clientSurface: screenContext?.surface || null,
            uiMode: 'text',
          },
        });
      }
      if (response?.proposedAction) {
        void trackAiEvent({
          type: AI_UI_EVENT_TYPES.PROPOSAL_SHOWN,
          role,
          routeKey: screenContext?.routeKey,
          capabilityKey: response.proposedAction.capabilityKey,
          actionId: response.proposedAction.id,
          resultKind: response?.assistantMessage?.kind || null,
          metadata: {
            clientSurface: screenContext?.surface || null,
            uiMode: 'text',
          },
        });
      }
      if (!contentOverride) {
        setDraft('');
      }
      // Append new messages to local state — no full session reload needed
      const newMessages = [response.userMessage, response.assistantMessage].filter(Boolean);
      if (newMessages.length) {
        setMessages((prev) => [...prev, ...newMessages]);
      }
      // If a proposed action was created, add it to session.actions so activeProposal updates
      if (response.proposedAction) {
        setSession((prev) => prev ? { ...prev, actions: [...(prev.actions || []), response.proposedAction] } : prev);
      }
      if (response.result?.type) {
        setLastHandoffRoute(null);
      } else if (response.handoffRoute) {
        setLastHandoffRoute(response.handoffRoute);
        setStatusMessage(getPreparedMessage(response.handoffRoute.routeKey));
      }
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to send AI request.');
      toast({
        title: error?.message || 'Failed to send AI request',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!activeProposal?.id) return;
    if (!activeProposal.confirmationToken) {
      setErrorMessage('Confirmation token expired. Please ask the assistant to prepare this action again.');
      return;
    }

    setBusyActionId(activeProposal.id);
    setErrorMessage('');
    setStatusMessage('');
    try {
      const response = await confirmAiAction(activeProposal.id, activeProposal.confirmationToken);
      void trackAiEvent({
        type: AI_UI_EVENT_TYPES.PROPOSAL_CONFIRMED,
        role,
        routeKey: screenContext?.routeKey,
        capabilityKey: activeProposal.capabilityKey,
        actionId: activeProposal.id,
        resultKind: response?.assistantMessage?.kind || 'result',
        metadata: {
          clientSurface: screenContext?.surface || null,
          uiMode: 'text',
        },
      });
      const sessionId = getAiSessionId(role);
      if (sessionId) {
        await loadSession(sessionId);
      }
      if (response.handoffRoute) {
        setLastHandoffRoute(response.handoffRoute);
        if (preferences.autoOpenPreparedScreens) {
          handleHandoff(response.handoffRoute);
        } else {
          setStatusMessage(getPreparedMessage(response.handoffRoute.routeKey));
        }
      } else {
        setStatusMessage('The action completed successfully.');
      }
    } catch (error) {
      const status = getErrorStatus(error);
      if (status === 410) {
        // Token expired — mark the proposal as expired so activeProposal clears
        setSession((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            actions: (prev.actions || []).map((a) =>
              a.id === activeProposal.id ? { ...a, status: 'expired' } : a,
            ),
          };
        });
        setErrorMessage('This action has expired. Please ask the assistant again.');
      } else if (status === 409) {
        // Already confirmed — remove the stale proposal from the UI
        setSession((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            actions: (prev.actions || []).filter((a) => a.id !== activeProposal.id),
          };
        });
        setErrorMessage('This action was already confirmed.');
      } else {
        setErrorMessage(error?.message || 'Failed to confirm AI action.');
      }
      const toastMsg = status === 410
        ? 'This action has expired. Please ask the assistant again.'
        : status === 409
          ? 'This action was already confirmed.'
          : (error?.message || 'Failed to confirm AI action');
      toast({ title: toastMsg, status: 'error', duration: 3000 });
    } finally {
      setBusyActionId(null);
    }
  };

  const handleCancel = async () => {
    if (!activeProposal?.id) {
      return;
    }

    setBusyActionId(activeProposal.id);
    setErrorMessage('');
    setStatusMessage('');
    try {
      await cancelAiAction(activeProposal.id);
      void trackAiEvent({
        type: AI_UI_EVENT_TYPES.PROPOSAL_CANCELED,
        role,
        routeKey: screenContext?.routeKey,
        capabilityKey: activeProposal.capabilityKey,
        actionId: activeProposal.id,
        metadata: {
          clientSurface: screenContext?.surface || null,
          uiMode: 'text',
        },
      });
      const sessionId = getAiSessionId(role);
      if (sessionId) {
        await loadSession(sessionId);
      }
      setStatusMessage('The proposed action was canceled.');
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to cancel AI action.');
      toast({
        title: error?.message || 'Failed to cancel AI action',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setBusyActionId(null);
    }
  };

  const handleClearHistory = async () => {
    const sessionId = getAiSessionId(role);
    setLoading(true);
    setErrorMessage('');
    setStatusMessage('');

    try {
      if (sessionId) {
        try {
          await deleteAiSession(sessionId);
        } catch (error) {
          if (error?.response?.status !== 404) {
            throw error;
          }
        }
      }

      clearAiSessionId(role);
      setSession(null);
      setMessages([]);
      setMessageCount(0);
      setOldestLoadedIndex(0);
      setLastHandoffRoute(null);
      setDraft('');
      void trackAiEvent({
        type: AI_UI_EVENT_TYPES.SESSION_CLEARED,
        role,
        routeKey: screenContext?.routeKey,
        metadata: {
          clientSurface: screenContext?.surface || null,
          uiMode: 'text',
        },
      });
      setStatusMessage('Assistant history cleared. Start a new request when you are ready.');
      toast({
        title: 'Assistant history cleared',
        status: 'success',
        duration: 2500,
      });
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to clear assistant history.');
      toast({
        title: error?.message || 'Failed to clear assistant history',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const helperText = capabilities.slice(0, 3).map((capability) => capability.title).join(', ');

  const handleOpenDrawer = () => {
    void trackAiEvent({
      type: AI_UI_EVENT_TYPES.LAUNCHER_OPENED,
      role,
      routeKey: screenContext?.routeKey,
      metadata: {
        clientSurface: screenContext?.surface || null,
        uiMode: 'text',
      },
    });
    onOpen();
  };

  if (!assistantEnabled || !serverAiEnabled || !userAiEnabled || !preferences.showLaunchers) {
    return null;
  }

  return (
    <>
      <Button leftIcon={<LuSparkles />} colorScheme="blue" onClick={handleOpenDrawer}>
        {buttonLabel}
      </Button>

      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <Stack spacing={1}>
              <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="0.2em" color="blue.500">
                {title}
              </Text>
              <Text fontSize="sm" fontWeight="400" color="gray.600">
                {subtitle}
              </Text>
            </Stack>
          </DrawerHeader>

          <DrawerBody ref={scrollContainerRef}>
            <VStack align="stretch" spacing={4} py={4}>
              {errorMessage ? (
                <Box borderWidth="1px" borderRadius="2xl" borderColor="orange.200" bg="orange.50" px={4} py={3}>
                  <Text fontSize="sm" color="gray.700">
                    {errorMessage}
                  </Text>
                </Box>
              ) : null}

              {statusMessage ? (
                <Box borderWidth="1px" borderRadius="2xl" borderColor="gray.200" bg="gray.50" px={4} py={3}>
                  <Text fontSize="sm" color="gray.700">
                    {statusMessage}
                  </Text>
                </Box>
              ) : null}

              {lastHandoffRoute ? (
                <Box borderWidth="1px" borderRadius="2xl" borderColor="blue.200" bg="blue.50" px={4} py={3}>
                  <Text fontSize="sm" color="gray.700">
                    {getPreparedMessage(lastHandoffRoute.routeKey)}
                  </Text>
                </Box>
              ) : null}

              {hasMoreMessages ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadOlderMessages}
                  isLoading={loadingMore}
                  w="100%"
                >
                  Load earlier messages
                </Button>
              ) : null}

              {!loading && !messages.length ? (
                <Box borderWidth="1px" borderRadius="2xl" borderColor="gray.200" bg="gray.50" px={4} py={4}>
                  <Text fontWeight="700" color="navy.700">
                    Start with a natural language request
                  </Text>
                  <Text mt={1} fontSize="sm" color="gray.600">
                    {helperText
                      ? `Available now: ${helperText}.`
                      : 'The assistant is ready for contextual help on this screen.'}
                  </Text>
                </Box>
              ) : null}

              {loading && !session ? (
                <Text fontSize="sm" color="gray.500">
                  Loading assistant…
                </Text>
              ) : null}

              {messages.map((message) => (
                <React.Fragment key={message.id}>
                  <AssistantMessage message={message} />
                  {message.role === 'assistant' && message.metadata?.result?.type ? (
                    <AiResultRenderer
                      result={message.metadata.result}
                      role={role}
                      navigate={navigate}
                      onTriggerMessage={handleSend}
                    />
                  ) : null}
                </React.Fragment>
              ))}

              {loading && session && (
                <HStack spacing={2} px={4} py={2} alignSelf="flex-start">
                  <Spinner size="sm" color="blue.400" />
                  <Text fontSize="sm" color="gray.500">Thinking...</Text>
                </HStack>
              )}

              {activeProposal ? (
                <ProposalCard
                  action={activeProposal}
                  onConfirm={handleConfirm}
                  onCancel={handleCancel}
                  busy={busyActionId === activeProposal.id}
                />
              ) : null}

              {(activeProposal?.handoffRoute || lastHandoffRoute) && !lastResult?.type ? (
                <Button
                  variant="outline"
                  colorScheme="blue"
                  onClick={() => handleHandoff(activeProposal?.handoffRoute || lastHandoffRoute)}
                >
                  {getPreparedButtonLabel((activeProposal?.handoffRoute || lastHandoffRoute)?.routeKey)}
                </Button>
              ) : null}

              <div ref={scrollAnchorRef} />
            </VStack>
          </DrawerBody>

          <DrawerFooter borderTopWidth="1px">
            <VStack align="stretch" spacing={3} w="100%">
              {capabilities.length ? (
                <HStack spacing={2} flexWrap="wrap">
                  {capabilities.slice(0, 3).map((capability) => (
                    <Badge key={capability.key} colorScheme="blue" borderRadius="full" px={3} py={1}>
                      {capability.title}
                    </Badge>
                  ))}
                </HStack>
              ) : null}
              <Textarea
                minH="50px"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (draft.trim() && !loading) {
                      void handleSend();
                    }
                  }
                }}
                placeholder="Describe what you want to do."
              />
              <HStack spacing={2}>
                <Button flex={1} colorScheme="blue" onClick={() => void handleSend()} isLoading={loading} isDisabled={!draft.trim()}>
                  Send to assistant
                </Button>
                <IconButton
                  aria-label="Voice assistant"
                  icon={<FiMic />}
                  onClick={() => setVoiceOpen(true)}
                  variant="ghost"
                  colorScheme="blue"
                  size="md"
                  isDisabled={loading}
                />
                <IconButton
                  aria-label="Clear history"
                  icon={<FiTrash2 />}
                  onClick={handleClearHistory}
                  variant="ghost"
                  colorScheme="gray"
                  size="md"
                  isLoading={loading}
                  title="Clear history"
                />
              </HStack>
            </VStack>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <AiVoiceModal
        isOpen={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        role={role}
        sessionId={session?.id}
        screenContext={screenContext}
        onFallbackToText={(transcriptDraft) => {
          setVoiceOpen(false);
          if (transcriptDraft) setDraft(transcriptDraft);
        }}
      />
    </>
  );
}
